import type { Component, VoidComponent } from "solid-js";
import { createMemo, For, Show } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import { useNavigate } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { useCustomerState } from "../../../state/customer";
import SettingsPageHeader from "./components/SettingsPageHeader.tsx";
import { useTranslation } from "../../../state/i18n";
import { isEmpty } from "lodash-es";
import { TextAndIconButton } from "../../../components/button/Button";
import ApiService from "../../../services/ApiService";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { MAX_WEBHOOKS } from "../../../constants";
import { Icon } from "../../../components/icon/Icon";
import EmptyState from "../../../components/emptyState/EmptyState";
import Pill from "../../../components/pill/Pill";
import { Toggle } from "../../../components/toggle/Toggle";
import { IconButtonMenu } from "../../../components/listItemMenu/ListItemMenu";
import { useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import UpsertWebhookModal from "./UpsertWebhookModal";
import DeleteWebhookModal from "./DeleteWebhookModal";
import Text from "../../../components/text/Text";
import withPageProductFeatureCheck from "../../../utils/withPageProductFeatureCheck";

const MAX_EVENT_PILLS = 2;

const WebhooksPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, replace } = useModalManager();
  const customer = useCustomerState();
  const webhooksQuery = ApiService.getWebhooks();
  const hasReachedWebhooksLimit = createMemo(() =>
    webhooksQuery.status === "success" ? webhooksQuery.data.length >= MAX_WEBHOOKS : false,
  );

  const openCreateWebhookModal = () => {
    open({
      content: () => {
        const createWebhookMutation = ApiService.createWebhook();

        return (
          <UpsertWebhookModal
            title={t(($) => $.SETTINGS_WEBHOOKS_CREATE_MODAL_TITLE)}
            confirmLabel={t(($) => $.SETTINGS_WEBHOOKS_CREATE_MODAL_ACTIONS_CONFIRM)}
            onConfirm={({ name, type, url }) =>
              new Promise((resolve, reject) => {
                createWebhookMutation.mutate(
                  { name, url, type, status: "ACTIVE" },
                  {
                    onSuccess: () => {
                      resolve();
                    },
                    onError: (err) => {
                      replace({
                        content: () => <GenericFailureModal error={err} />,
                      });
                      reject(err);
                    },
                  },
                );
              })
            }
          />
        );
      },
    });
  };

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_CONNECTIONS_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_WEBHOOKS_LABEL)}
        actionSlot={() => (
          <Show when={!isEmpty(webhooksQuery.data) && !hasReachedWebhooksLimit()}>
            <div class="ml-auto">
              <TextAndIconButton
                variant="light"
                size="large"
                text={t(($) => $.SETTINGS_WEBHOOKS_CREATE_BUTTON_LABEL)}
                icon="plus"
                onClick={() => {
                  openCreateWebhookModal();
                }}
              />
            </div>
          </Show>
        )}
      />

      <Show when={hasReachedWebhooksLimit()}>
        <div class="pt-6">
          <InlineMessage
            variant="warning"
            label={t(($) => $.SETTINGS_WEBHOOKS_LIMIT_REACHED, { limit: MAX_WEBHOOKS })}
          />
        </div>
      </Show>

      <div class="relative h-full min-h-0" aria-busy={webhooksQuery.isFetching}>
        <Show
          when={webhooksQuery.data}
          fallback={
            <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
              <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
              <span class="text-medium-regular text-primary">{t(($) => $.SETTINGS_WEBHOOKS_LOADING_TITLE)}</span>
            </div>
          }
        >
          {(data) => (
            <div class="h-full pt-8">
              <Show when={!isEmpty(data())} fallback={<NoWebhooks actionOnClick={openCreateWebhookModal} />}>
                <div
                  role="table"
                  style={{
                    "--column-template": "64px minmax(120px, 1fr) 240px 30px",
                  }}
                  class="flex size-full border-collapse flex-col overflow-hidden"
                >
                  <div role="rowgroup">
                    <div
                      role="row"
                      class="grid grid-cols-template gap-6 border-b border-subtle px-3 py-1.5 text-base-semibold text-secondary"
                    >
                      <div role="columnheader">{t(($) => $.SETTINGS_WEBHOOKS_TABLE_HEADER_STATUS)}</div>
                      <div role="columnheader">{t(($) => $.SETTINGS_WEBHOOKS_TABLE_HEADER_NAME)}</div>
                      <div role="columnheader">{t(($) => $.SETTINGS_WEBHOOKS_TABLE_HEADER_EVENTS)}</div>
                      <div role="columnheader" class="sr-only">
                        {t(($) => $.SETTINGS_WEBHOOKS_TABLE_HEADER_ACTIONS)}
                      </div>
                    </div>
                  </div>

                  <div role="rowgroup" class="h-full overflow-y-auto pb-6">
                    <For each={data()}>
                      {(webhook) => {
                        const updateWebhookMutation = ApiService.updateWebhook(webhook.id);
                        const webhookPageUrl = `/customers/${customer.customerId}/settings/webhooks/${webhook.id}`;

                        return (
                          <div
                            role="row"
                            class="relative grid grid-cols-template items-center gap-6 overflow-hidden bg-surface-default px-3 py-4 text-base-regular text-secondary [&:not(:has([class*='bg-']:hover))]:hover:bg-surface-hover"
                          >
                            <a
                              href={webhookPageUrl}
                              class="absolute top-0 left-0 block size-full cursor-pointer -outline-offset-1 focus-visible:outline-default"
                              aria-label={webhook.name}
                            />

                            <div class="pointer-events-none contents">
                              <div role="cell">
                                <div class="pointer-events-auto relative">
                                  <Toggle
                                    aria-label={
                                      webhook.enabled
                                        ? t(($) => $.SETTINGS_WEBHOOKS_TABLE_ACTIONS_DISABLE)
                                        : t(($) => $.SETTINGS_WEBHOOKS_TABLE_ACTIONS_ENABLE)
                                    }
                                    checked={webhook.enabled}
                                    setChecked={(value) => {
                                      updateWebhookMutation.mutate({
                                        id: webhook.id.toString(),
                                        name: webhook.name,
                                        url: webhook.url,
                                        status: value ? "ACTIVE" : "DISABLED",
                                        type: webhook.type,
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div role="cell" class="flex flex-col">
                                <div class="flex items-center gap-2">
                                  <Text class="text-base-medium text-primary" text={webhook.name} truncate />
                                  <Show
                                    when={webhook.enabled}
                                    fallback={
                                      <Pill variant="neutral" label={t(($) => $.SETTINGS_WEBHOOKS_STATUS_DISABLED)} />
                                    }
                                  >
                                    <Pill variant="success" label={t(($) => $.SETTINGS_WEBHOOKS_STATUS_ENABLED)} />
                                  </Show>
                                </div>
                                <Text class="text-base-regular text-secondary" text={webhook.url} truncate />
                              </div>
                              <div role="cell">
                                <For each={webhook.type.slice(0, MAX_EVENT_PILLS)}>
                                  {(type) => <Pill variant="neutral" label={type} />}
                                </For>
                                <Show when={webhook.type.length > MAX_EVENT_PILLS}>
                                  <span class="pl-1">
                                    <Pill variant="neutral" label={`+${webhook.type.length - MAX_EVENT_PILLS}`} />
                                  </span>
                                </Show>
                              </div>
                              <div role="cell" class="pointer-events-auto relative">
                                <IconButtonMenu
                                  aria-label={t(($) => $.ACTIONS_ARIA)}
                                  items={[
                                    [
                                      {
                                        value: "edit",
                                        type: "action",
                                        variant: "icon",
                                        label: t(($) => $.SETTINGS_WEBHOOKS_TABLE_ACTIONS_EDIT),
                                        icon: { name: "pencilAlt", type: "outline" },
                                        onClick: async () => {
                                          open({
                                            content: () => (
                                              <UpsertWebhookModal
                                                webhook={webhook}
                                                title={t(($) => $.SETTINGS_WEBHOOKS_EDIT_MODAL_TITLE)}
                                                confirmLabel={t(($) => $.SETTINGS_WEBHOOKS_EDIT_MODAL_ACTIONS_CONFIRM)}
                                                onConfirm={({ name, url, type }) =>
                                                  new Promise((resolve, reject) => {
                                                    updateWebhookMutation.mutate(
                                                      {
                                                        id: webhook.id.toString(),
                                                        name,
                                                        url,
                                                        type,
                                                        status: webhook.enabled ? "ACTIVE" : "DISABLED",
                                                      },
                                                      {
                                                        onSuccess: () => {
                                                          resolve();
                                                        },
                                                        onError: (err) => {
                                                          replace({
                                                            content: () => <GenericFailureModal error={err} />,
                                                          });
                                                          reject(err);
                                                        },
                                                      },
                                                    );
                                                  })
                                                }
                                              />
                                            ),
                                          });
                                        },
                                      },
                                      {
                                        value: "reconnect",
                                        type: "action",
                                        variant: "icon",
                                        label: t(($) => $.SETTINGS_WEBHOOKS_TABLE_ACTIONS_VIEW),
                                        icon: { name: "eye", type: "outline" },
                                        onClick: async () => {
                                          navigate(webhookPageUrl);
                                        },
                                      },
                                      {
                                        value: "delete",
                                        type: "action",
                                        variant: "icon",
                                        label: t(($) => $.SETTINGS_WEBHOOKS_TABLE_ACTIONS_DELETE),
                                        icon: { name: "trash", type: "outline" },
                                        onClick: async () => {
                                          open({
                                            content: () => <DeleteWebhookModal webhookId={webhook.id} />,
                                          });
                                        },
                                      },
                                    ],
                                  ]}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    </For>
                  </div>
                </div>
              </Show>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

type NoWebhooksProps = {
  actionOnClick: () => void;
};
const NoWebhooks: VoidComponent<NoWebhooksProps> = (props) => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.SETTINGS_WEBHOOKS_TABLE_EMPTY_STATE_TITLE)}
      description={t(($) => $.SETTINGS_WEBHOOKS_TABLE_EMPTY_STATE_DESCRIPTION)}
      illustration={{ primaryIcon: "webhook" }}
      action={{
        variant: "light",
        icon: "plus",
        text: t(($) => $.SETTINGS_WEBHOOKS_TABLE_EMPTY_STATE_ACTION_LABEL),
        onClick: () => {
          props.actionOnClick();
        },
      }}
    />
  );
};

const ProtectedWebhooksPage = withPageProductFeatureCheck(
  withPagePermissionCheck(WebhooksPage, PermissionService.canManageWebhooks),
  "WEBHOOKS",
);

export default ProtectedWebhooksPage;
